import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { JSX, MouseEvent, ReactNode } from "react";
import { Link } from "react-router-dom";

import { XOP_SLOGAN } from "../../lib/company.js";
import { ThemeToggleSegment } from "../../components/ui/theme-toggle-segment.js";

/**
 * A click on the mark while already at `/` scrolls to the top (smoothly,
 * unless the visitor asked for reduced motion) and swallows the
 * navigation, which the router would otherwise treat as a no-op that
 * still pushes a history entry. Exported for the unit test.
 */
export function scrollToTopIfHome(event: MouseEvent<HTMLAnchorElement>): void {
  if (window.location.pathname !== "/") return;
  event.preventDefault();
  const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  window.scrollTo({ top: 0, behavior: reduce ? "auto" : "smooth" });
}

/**
 * The white header strip, adapted from the customer support portal's
 * `AuthLayout` header (xopnetworks.com wears the same one, which is the
 * point — a reseller arriving from the marketing site should recognise the
 * roof before they read a word).
 *
 * Structure is the reference's: dark-ink mark, a hairline divider, then the
 * company slogan in small wide-tracked caps.
 *
 * The reference forces light tokens onto the strip with a `surface-light`
 * island because its portal is dark-first. We do not need that trick — this
 * app's MUI theme is the light one — but the intent still has to be pinned:
 * the bar is deliberately white *regardless* of the theme mode, because the
 * only logo we have for it is the dark-ink `/xop-logo.svg` (the white-ink
 * `-dark` variant would vanish). So the colours below are taken from
 * `common.white` / `common.black` rather than from `background.paper` /
 * `text.primary`, which would follow a future dark mode and break the mark.
 *
 * `children` is the public site's navigation (`pages/public/SiteNav.tsx`);
 * the wall and the invitation page pass nothing and render as before.
 */
export function TopBar({ children }: { children?: ReactNode } = {}): JSX.Element {
  return (
    <Box
      component="header"
      sx={{
        position: "relative",
        zIndex: 3,
        bgcolor: "common.white",
        borderBottom: 1,
        borderColor: (t) => alpha(t.palette.common.black, 0.08),
        boxShadow: (t) => `0 1px 3px ${alpha(t.palette.common.black, 0.06)}`,
      }}
    >
      <Box
        sx={{
          mx: "auto",
          maxWidth: 1380,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 3,
          px: { xs: 2.5, sm: 5, lg: 7 },
          height: { xs: 60, lg: 78 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 2, sm: 3.5 },
            minWidth: 0,
            flexShrink: 0,
          }}
        >
          {/* The mark is the site's Home button, as on every site: it links
              to `/`, and when the visitor is ALREADY on `/` it scrolls back
              to the top instead — a route change to the same path is a
              no-op for the router, so `PublicLayout`'s scroll-on-navigate
              never fires for it. `aria-label` names the destination rather
              than the picture, which is what a screen reader wants from a
              link. */}
          <Link
            to="/"
            aria-label="XOP Networks — home"
            onClick={scrollToTopIfHome}
            style={{ display: "block", lineHeight: 0 }}
          >
            <Box
              component="img"
              src="/xop-logo.svg"
              alt="XOP Networks"
              draggable={false}
              // Matches the reference bar exactly: 3.4 / 3.9 / 4.4rem.
              sx={{ height: { xs: 54.4, sm: 62.4, lg: 70.4 }, width: "auto", display: "block" }}
            />
          </Link>
          <Box
            aria-hidden
            sx={{
              display: { xs: "none", sm: "block" },
              width: "1px",
              alignSelf: "stretch",
              my: 2.5,
              bgcolor: (t) => alpha(t.palette.common.black, 0.18),
            }}
          />
          <Typography
            component="p"
            sx={{
              // Below `lg` the slogan yields to the navigation `children`
              // slot so the bar never wraps to a second line.
              display: { xs: "none", sm: children ? "none" : "block", lg: "block" },
              whiteSpace: "nowrap",
              fontSize: { sm: 11, lg: 12 },
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.32em",
              color: (t) => alpha(t.palette.common.black, 0.55),
            }}
          >
            {XOP_SLOGAN}
          </Typography>
        </Box>

        {children ? (
          <Box sx={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, justifyContent: "flex-end" }}>
            {children}
          </Box>
        ) : null}

        {/* Far right, as in the reference. The bar is the only chrome on the
            page, so it is the only place a theme control can live. */}
        <ThemeToggleSegment />
      </Box>
    </Box>
  );
}
